import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { bool, func, shape } from 'prop-types';
import { MDialog } from 'common';
import { CUSTOMER_AUTH_TYPE_OPTIONS } from 'feature/loading-management/utils/const';
import { sanitizeVolume, volumeError } from './validators';

// Auth types that show no volume fields
const NO_VOLUME_TYPES = ['UNLIMITED', 'SPECIFIC'];
// Auth types that show both Authorization and Renewal volume fields
const BOTH_VOLUME_TYPES = ['MONTHLY', 'WEEKLY', 'DAILY'];

const AuthTypeAndVolumes = ({
  customerAuth,
  isReadOnly,
  setCustomerAuth,
  setValidation,
  validation,
}) => {
  const { authorizationType, authorizationVolume, renewalVolume } = customerAuth;
  const [pendingAuthType, setPendingAuthType] = useState(null);

  const isValidType = CUSTOMER_AUTH_TYPE_OPTIONS.some(({ value }) => value === authorizationType);
  const isTypeMissing = R.not(R.isNil(authorizationType)) && R.not(isValidType);

  useEffect(() => {
    setValidation(current =>
      R.assoc('authorizationType', authorizationType ? '' : 'Required', current)
    );
  }, [authorizationType, setValidation]);

  useEffect(() => {
    if (isTypeMissing) {
      setCustomerAuth(R.assoc('authorizationType', undefined));
      setValidation(R.assoc('authorizationType', 'Required'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTypeMissing]);

  const showAuthVolume =
    R.not(R.isNil(authorizationType)) && !NO_VOLUME_TYPES.includes(authorizationType);
  const showRenewalVolume = BOTH_VOLUME_TYPES.includes(authorizationType);
  const isOneLoad = authorizationType === 'ONELOAD';

  useEffect(() => {
    setValidation(current => ({
      ...current,
      authorizationVolume: showAuthVolume && !isOneLoad ? volumeError(authorizationVolume) : '',
      renewalVolume: showRenewalVolume ? volumeError(renewalVolume) : '',
    }));
  }, [
    authorizationVolume,
    isOneLoad,
    renewalVolume,
    setValidation,
    showAuthVolume,
    showRenewalVolume,
  ]);

  const commitAuthType = value => {
    setCustomerAuth(current => {
      const updated = R.assoc('authorizationType', value, current);
      const withAuthVolume = R.assoc(
        'authorizationVolume',
        value === 'ONELOAD' ? 1 : undefined,
        updated
      );
      const withRenewalVolume = R.assoc('renewalVolume', undefined, withAuthVolume);
      if (value === 'SPECIFIC') {
        return R.assoc('products', [], withRenewalVolume);
      }
      return withRenewalVolume;
    });
    setValidation(current => ({
      ...current,
      authorizationType: '',
      authorizationVolume: '',
      renewalVolume: '',
    }));
  };

  const handleAuthTypeChange = e => {
    const { value } = e.target;
    if (authorizationType === 'SPECIFIC' && value !== 'SPECIFIC') {
      setPendingAuthType(value);
      return;
    }
    commitAuthType(value);
  };

  const handleConfirmAuthTypeChange = () => {
    commitAuthType(pendingAuthType);
    setCustomerAuth(current => R.assoc('products', [], current));
    setPendingAuthType(null);
  };

  const handleVolumeChange = field => e => {
    const value = sanitizeVolume(e.target.value);
    setCustomerAuth(R.assoc(field, value));
    setValidation(R.assoc(field, volumeError(value)));
  };

  return (
    <Stack direction="row" spacing={2}>
      <FormControl
        disabled={isReadOnly}
        error={!!validation.authorizationType}
        required
        sx={{ minWidth: 220 }}
      >
        <InputLabel id="authorization-type-label">Authorization Type</InputLabel>
        <Select
          disabled={isReadOnly}
          label="Authorization Type"
          labelId="authorization-type-label"
          onChange={handleAuthTypeChange}
          value={authorizationType ?? ''}
        >
          {CUSTOMER_AUTH_TYPE_OPTIONS.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showAuthVolume && (
        <TextField
          disabled={isReadOnly || isOneLoad}
          error={!!validation.authorizationVolume}
          helperText={validation.authorizationVolume}
          inputProps={{ inputMode: 'numeric' }}
          label="Authorization Volume"
          onChange={handleVolumeChange('authorizationVolume')}
          required
          sx={{ maxWidth: 200 }}
          value={authorizationVolume ? Number(authorizationVolume).toLocaleString() : ''}
        />
      )}
      {showRenewalVolume && (
        <TextField
          disabled={isReadOnly}
          error={!!validation.renewalVolume}
          helperText={validation.renewalVolume}
          inputProps={{ inputMode: 'numeric' }}
          label="Renewal Volume"
          onChange={handleVolumeChange('renewalVolume')}
          required
          sx={{ maxWidth: 200 }}
          value={renewalVolume ? Number(renewalVolume).toLocaleString() : ''}
        />
      )}

      <MDialog
        header="Update Authorization Type?"
        leftButtonAction={() => setPendingAuthType(null)}
        leftButtonText="Back"
        onClose={() => setPendingAuthType(null)}
        open={R.not(R.isNil(pendingAuthType))}
        rightButtonAction={handleConfirmAuthTypeChange}
        rightButtonText="Confirm"
      >
        Changing from a Product Specific authorization to another will remove all products currently
        configured for this authorization.
      </MDialog>
    </Stack>
  );
};

AuthTypeAndVolumes.propTypes = {
  customerAuth: shape({}),
  isReadOnly: bool,
  setCustomerAuth: func.isRequired,
  setValidation: func.isRequired,
  validation: shape({}).isRequired,
};

AuthTypeAndVolumes.defaultProps = {
  customerAuth: {},
  isReadOnly: false,
};

export default AuthTypeAndVolumes;
